import {
  CoverageForm,
  CoverageLimitType,
  InsuranceCoverageSource,
  InsuranceMonitorConfiguration,
  InsuranceMonitorConfigurationsApiCreateInsuranceMonitorConfigurationRequest,
  InsurancePolicyType
} from "@wingspanhq/wingspan-api-sdk";
import * as Yup from "yup";

import { RequirementDefinitionGeneralLiabilityFormValues } from "../../../../types";
import { getBaseEditValues } from "../../../../utils/getBaseEditValues";
import { RequirementDefinitionTemplateForm } from "../../types";
import { FormPartialAcceptWingspanMasterPolicy } from "../FormPartialAcceptWingspanMasterPolicy";
import {
  FormPartialGeneralLiability,
  GENERAL_LIABILITY_AGGREGATE_MINIMUM_COMMON,
  GENERAL_LIABILITY_PER_OCCURRENCE_MINIMUM_COMMON
} from "../FormPartialGeneralLiability";
import { WarningWingspanIsNotAgent } from "../WarningWingspanIsNotAgent";

/*
https://github.com/wingspanHQ/benefits/blob/staging/src/lib/interfaces/insuranceRequirement.ts
*/

export const GENERAL_LIABILITY_FORM: RequirementDefinitionTemplateForm<
  InsuranceMonitorConfiguration,
  RequirementDefinitionGeneralLiabilityFormValues,
  InsuranceMonitorConfigurationsApiCreateInsuranceMonitorConfigurationRequest
> = {
  getDefaultSettingsValues: template => ({
    acceptWingspanMasterPolicy: true,
    coverageType: "occurrence",
    name: template.name,
    perIncidentLimitMinimum: `${GENERAL_LIABILITY_PER_OCCURRENCE_MINIMUM_COMMON}`,
    totalCoverageLimitMinimum: `${GENERAL_LIABILITY_AGGREGATE_MINIMUM_COMMON}`
  }),
  getEditSettingsValues(template, requirementDefinition, settings) {
    const coverage = settings.coverages?.find(
      c => c.policyType === InsurancePolicyType.GENERAL_LIABILITY
    );
    const perIncident = coverage?.limits?.find(
      l => l.limitType === CoverageLimitType.PER_ACCIDENT
    );
    const aggregate = coverage?.limits?.find(
      l => l.limitType === CoverageLimitType.POLICY_AGGREGATE
    );
    const acceptWingspan =
      coverage?.acceptedSourceTypes?.includes(
        InsuranceCoverageSource.PLAN_ENROLLMENT
      ) ?? false;
    const coverageType =
      coverage?.coverageForm === CoverageForm.CLAIMS_MADE
        ? "claimsMade"
        : "occurrence";

    return {
      acceptWingspanMasterPolicy: acceptWingspan,
      coverageType,
      perIncidentLimitMinimum: String(perIncident?.amount ?? 0),
      totalCoverageLimitMinimum: String(aggregate?.amount ?? 0)
    };
  },
  mapDataSourceResponseToRequirementCreateRequest(response) {
    return {
      dataSourceId: response.id!
    };
  },
  mapFormDataToDataSourceCreateRequest(values) {
    const {
      acceptWingspanMasterPolicy,
      totalCoverageLimitMinimum,
      perIncidentLimitMinimum,
      coverageType
    } = values;

    return {
      insuranceMonitorConfigurationCreate: {
        coverages: [
          {
            acceptedSourceTypes: acceptWingspanMasterPolicy
              ? [
                  InsuranceCoverageSource.INSURANCE_IMPORT,
                  InsuranceCoverageSource.PLAN_ENROLLMENT
                ]
              : [InsuranceCoverageSource.INSURANCE_IMPORT],
            appliedProvisions: [],
            coverageForm:
              coverageType === "claimsMade"
                ? CoverageForm.CLAIMS_MADE
                : CoverageForm.OCCURRENCE,
            limits: [
              {
                amount: Number(perIncidentLimitMinimum),
                currency: "USD",
                limitType: CoverageLimitType.PER_ACCIDENT
              },
              {
                amount: Number(totalCoverageLimitMinimum),
                currency: "USD",
                limitType: CoverageLimitType.POLICY_AGGREGATE
              }
            ],
            policyType: InsurancePolicyType.GENERAL_LIABILITY
          }
        ]
      }
    };
  },
  partialForm: (
    <>
      <WarningWingspanIsNotAgent />
      <FormPartialAcceptWingspanMasterPolicy switchLabel="Accept Wingspan's PL/GL Master Policy" />
      <FormPartialGeneralLiability />
    </>
  ),
  successCreationMessage: "Requirement created successfully",
  validationSchema: Yup.object().shape({
    acceptWingspanMasterPolicy: Yup.boolean().required("Required"),
    coverageType: Yup.string()
      .oneOf(["occurrence", "claimsMade"])
      .required("Required") as Yup.AnySchema,
    perIncidentLimitMinimum: Yup.string().required("Required"),
    totalCoverageLimitMinimum: Yup.string().required("Required")
  })
};
